import type { ReactNode } from 'react';
import { DotsSixVerticalIcon, PushPinSimpleIcon } from '@phosphor-icons/react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '@admin-panel/ui';
import type { OccasionTreeItemType } from '../../../../schemas/styling';
import { getOptimizedSquareImage } from '../../helpers';

type SortableOccasionItemProps = {
  occasion: OccasionTreeItemType;
  active?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  actionSlot: ReactNode;
};

export const OccasionStatusTag = ({ isActive }: { isActive?: boolean }) =>
  isActive === false ? (
    <Badge tone="warning" variant="subtle">
      Inactive
    </Badge>
  ) : (
    <Badge tone="success" variant="subtle">
      Active
    </Badge>
  );

const OccasionIconPreview = ({
  label,
  src,
  tone,
}: {
  label: string;
  src?: string;
  tone: 'light' | 'dark';
}) =>
  src ? (
    <div
      className={`flex h-5 w-5 items-center justify-center overflow-hidden rounded border ${
        tone === 'light' ? 'border-border bg-white' : 'border-border bg-neutral-950'
      }`}
      title={label}
    >
      <img
        alt={label}
        src={getOptimizedSquareImage(src, 48)}
        className="h-3.5 w-3.5 object-cover"
      />
    </div>
  ) : null;

const SortableOccasionItem = ({
  occasion,
  active,
  disabled = false,
  onSelect,
  actionSlot,
}: SortableOccasionItemProps) => {
  const isInactive = occasion.is_active === false;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: occasion.id,
    disabled,
  });

  return (
    <div
      ref={setNodeRef}
      onClick={onSelect}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : -1}
      onKeyDown={event => {
        if (!onSelect) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      style={{
        transform: CSS.Transform.toString(transform),
        transition: transition || undefined,
        cursor: isDragging ? 'grabbing' : undefined,
      }}
      className={`w-full rounded-xl border p-3 text-left transition-colors ${
        active
          ? 'border-foreground/40 bg-muted/45'
          : isInactive
            ? 'border-border bg-muted/20'
            : 'border-border bg-card hover:border-foreground/30'
      } ${isDragging ? 'opacity-70' : 'opacity-100'}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <button
            type="button"
            {...attributes}
            {...listeners}
            onClick={event => event.stopPropagation()}
            className={`inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted/30 text-muted-foreground ${
              disabled ? 'cursor-not-allowed opacity-60' : 'cursor-grab active:cursor-grabbing'
            }`}
          >
            <DotsSixVerticalIcon size={14} />
          </button>
          {occasion.image?.url ? (
            <img
              alt={occasion.name}
              src={getOptimizedSquareImage(occasion.image.url, 480)}
              className="aspect-2/3 w-10 rounded-md border border-border object-cover"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-md border border-border bg-muted/25 text-muted-foreground">
              <PushPinSimpleIcon size={14} />
            </div>
          )}

          <div className="min-w-0">
            <p
              className={`truncate text-sm font-medium ${
                isInactive ? 'text-muted-foreground' : 'text-foreground'
              }`}
            >
              {occasion.name}
            </p>
            <div className="mt-1 flex items-center gap-2">
              <div className="w-fit">
                <OccasionStatusTag isActive={occasion.is_active} />
              </div>
              <div className="flex items-center gap-1.5">
                <OccasionIconPreview
                  label={`${occasion.name} light icon`}
                  src={occasion.icon_light?.url}
                  tone="dark"
                />
                <OccasionIconPreview
                  label={`${occasion.name} dark icon`}
                  src={occasion.icon_dark?.url}
                  tone="light"
                />
              </div>
            </div>
          </div>
        </div>

        {actionSlot}
      </div>
    </div>
  );
};

export default SortableOccasionItem;
